import { SessionContext, SkillLevel, UserInterests } from '@/context/SessionContext';
import ConductionAPI from '@/javascript/conduction-api';
import { Artist, Genre } from '@/javascript/library-objects';
import { Instrument } from '@/javascript/music-objects';
import { Utils } from '@/javascript/utils';
import { Button, Group, Modal, MultiSelect, Select, Skeleton, Stack, } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useContext, useEffect, useState } from 'react';

interface UserInterestsModalProps {
	showModal: boolean;
	setShowModal: (show: boolean) => void;
}

const UserInterestsModal: React.FC<UserInterestsModalProps> = ({
	showModal,
	setShowModal,
}) => {
	const session = useContext(SessionContext);
	if (!session) throw new Error('Modals must be a child of SessionContext.');

	// state to hold fetched options
	const [genres, setGenres] = useState<Genre[] | null>(null);
	const [artists, setArtists] = useState<Artist[] | null>(null);
	const [instruments, setInstruments] = useState<Instrument[] | null>(null);

	// state to hold user-selected values
	const [selectedGenres, setSelectedGenres] = useState<string[]>(session.userInterests?.genres.map(g => g.genreName) || []);
	const [selectedArtists, setSelectedArtists] = useState<string[]>(session.userInterests?.artists.map(a => a.artistName) || []);
	const [selectedInstruments, setSelectedInstruments] = useState<string[]>(session.userInterests?.instruments.map(i => i.name) || []);
	const [skillLevel, setSkillLevel] = useState<SkillLevel>(session.userInterests?.skillLevel || null);

	// fetch genre/artist/instrument data on mount
	useEffect(() => {
		ConductionAPI.getGenres()
			.then((res) => {
				if (res) setGenres(res);
			})
			.catch(() => {
				Utils.displayNotification(
					"Network Error",
					"Failed to load genres. Please check your connection.",
					"red",
					<IconAlertTriangle size={16} />
				);
			});

		ConductionAPI.getArtists()
			.then((res) => {
				if (res) setArtists(res);
			})
			.catch(() => {
				Utils.displayNotification(
					"Network Error",
					"Failed to load artists. Please check your connection.",
					"red",
					<IconAlertTriangle size={16} />
				);
			});

		ConductionAPI.getInstruments()
			.then((res) => {
				if (res) setInstruments(res);
			})
			.catch(() => {
				Utils.displayNotification(
					"Network Error",
					"Failed to load instruments. Please check your connection.",
					"red",
					<IconAlertTriangle size={16} />
				);
			});
	}, []);

	// handle form submission
	const handleSubmit = () => {
		const userInterests: UserInterests =
		{
			genres: selectedGenres.map((genreId) => genres?.find((g) => g.genreName === genreId)).filter(genre => genre != undefined) as Genre[],
			artists: selectedArtists.map((artistId) => artists?.find((a) => a.artistName === artistId)).filter(artist => artist != undefined) as Artist[],
			instruments: selectedInstruments.map((instrumentName) => instruments?.find((i) => i.name === instrumentName)).filter(inst => inst != undefined) as Instrument[],
			skillLevel: skillLevel ?? undefined
		};

		session.setUserInterests(userInterests);

		// TODO: Save user interests to the backend

		// notification
		Utils.displayNotification(
			'Preferences Saved',
			'Your music preferences have been updated.',
			'teal',
			undefined,
		);

		setShowModal(false);
	};

	return (
		<Modal
			opened={showModal}
			onClose={() => setShowModal(false)}
			title="Customize Your Interests"
			centered
			size="xl"
			styles={{
				title: {
					fontSize: 26,
					fontVariant: 'all-small-caps',
				},
			}}
		>
			<Stack>
				{/* Favorite Genres */}
				{genres === null ? (
					<Skeleton height={56} radius="sm" />
				) : (
					<MultiSelect
						label="Favorite Genres"
						searchable
						placeholder="Select one or more genres"
						data={genres.map((g) => ({
							value: g.genreId,
							label: g.genreName,
						}))}
						value={selectedGenres}
						onChange={setSelectedGenres}
					/>
				)}

				{/* Favorite Artists */}
				{artists === null ? (
					<Skeleton height={56} radius="sm" />
				) : (
					<MultiSelect
						label="Favorite Artists"
						searchable
						placeholder="Select one or more artists"
						data={artists.map((a) => ({
							value: a.artistId,
							label: a.artistName,
						}))}
						value={selectedArtists}
						onChange={setSelectedArtists}
					/>
				)}

				{/* Preferred Instruments */}
				{instruments === null ? (
					<Skeleton height={56} radius="sm" />
				) : (
					<MultiSelect
						label="Preferred Instruments"
						placeholder="Select one or more instruments"
						searchable
						data={instruments.map((i) => ({
							value: i.id,
							label: i.name,
						}))}
						value={selectedInstruments}
						onChange={setSelectedInstruments}
					/>
				)}

				{/* Skill Level */}
				<Select
					label="Skill Level"
					placeholder="Choose your skill level"
					data={[
						{ value: 'Beginner', label: 'Beginner' },
						{ value: 'Intermediate', label: 'Intermediate' },
						{ value: 'Advanced', label: 'Advanced' },
					]}
					value={skillLevel}
					onChange={(value) => setSkillLevel(value as SkillLevel)}
				/>

				{/* Submit */}
				<Group justify="flex-end" mt="md">
					<Button
						onClick={handleSubmit}
						disabled={!genres || !artists || !instruments}
					>
						Save Preferences
					</Button>
				</Group>
			</Stack>
		</Modal>
	);
};

export default UserInterestsModal;
